import { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { ChevronRight, LogOut, Shield, Globe, CreditCard } from "lucide-react"
import { toast } from "sonner"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"

import { useAuthStore } from "@/stores/authStore"
import { APP_NAME, BILLING_ENABLED } from "@/lib/appConfig"
import { CURRENCIES } from "@/lib/constants"
import { api } from "@/lib/api"
import { PageHeader } from "@/components/shared/PageHeader"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

function getInitials(name: string) {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2)
}

interface ReminderSetting {
  id: string | null
  reminder_type: string
  enabled: boolean
  days_before: number
  send_to_tenant: boolean
}

const REMINDER_LABELS: Record<string, string> = {
  rent_due: "Rent due reminders",
  rent_overdue: "Overdue rent alerts",
  lease_expiry: "Lease expiry alerts",
  document_expiry: "Document expiry alerts",
}

function NotificationSettings() {
  const qc = useQueryClient()

  const { data: settings = [], isLoading } = useQuery<ReminderSetting[]>({
    queryKey: ["reminder-settings"],
    queryFn: async () => {
      const { data } = await api.get("/api/reminders")
      return data.data ?? []
    },
  })

  const toggle = useMutation({
    mutationFn: ({ type, enabled }: { type: string; enabled: boolean }) =>
      api.put(`/api/reminders/${type}`, { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["reminder-settings"] }),
    onError: () => toast.error("Failed to update setting"),
  })

  if (isLoading) {
    return (
      <div className="space-y-1">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-12 rounded-xl" />
        ))}
      </div>
    )
  }

  return (
    <Card className="rounded-3xl border border-border/50 bg-card/60 backdrop-blur-xl shadow-soft overflow-hidden">
      <CardContent className="p-0 divide-y divide-border/50">
        {settings.map((s) => (
          <div key={s.reminder_type} className="flex items-center justify-between px-5 py-4 hover:bg-muted/30 transition-colors">
            <Label htmlFor={s.reminder_type} className="text-sm font-semibold cursor-pointer">
              {REMINDER_LABELS[s.reminder_type] ?? s.reminder_type}
            </Label>
            <Switch
              id={s.reminder_type}
              checked={s.enabled}
              onCheckedChange={(checked) =>
                toggle.mutate({ type: s.reminder_type, enabled: checked })
              }
              className="data-[state=checked]:bg-primary shadow-sm"
            />
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

function BillingPortalButton() {
  const billingPortal = useMutation({
    mutationFn: async () => {
      const { data } = await api.post("/api/stripe/billing-portal")
      return data.data
    },
    onSuccess: ({ url }) => {
      window.location.href = url
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error ?? "Failed to open billing portal"
      toast.error(msg)
    },
  })

  return (
    <button
      onClick={() => billingPortal.mutate()}
      disabled={billingPortal.isPending}
      className="w-full flex items-center justify-between px-5 py-4 hover:bg-muted/50 transition-colors disabled:opacity-50"
    >
      <span className="text-sm font-semibold">{billingPortal.isPending ? "Opening..." : "Manage Billing & Invoices"}</span>
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
    </button>
  )
}

export default function SettingsPage() {
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()

  useEffect(() => {
    const prev = document.title
    document.title = `Settings | ${APP_NAME}`
    return () => { document.title = prev }
  }, [])

  function handleLogout() {
    logout()
    toast.success("Signed out")
    navigate("/login")
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader title="Settings" />

      <div className="flex-1 overflow-y-auto pb-nav space-y-5 px-4 pt-4">
        {/* Profile */}
        <section>
          <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-3 px-2">
            Profile
          </p>
          <Card className="rounded-3xl border border-border/50 bg-card/60 backdrop-blur-xl shadow-soft">
            <CardContent className="p-5">
              <div className="flex items-center gap-4 mb-5">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0 shadow-inner">
                  <span className="text-xl font-bold text-primary">
                    {user?.full_name ? getInitials(user.full_name) : "?"}
                  </span>
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-lg truncate tracking-tight">{user?.full_name ?? "—"}</p>
                  <p className="text-sm text-muted-foreground truncate font-medium">
                    {user?.email ?? "—"}
                  </p>
                  {user?.plan?.name && (
                    <span className="inline-flex mt-1.5 text-xs px-2.5 py-1 rounded-md bg-primary/10 text-primary font-bold capitalize">
                      {user.plan.name} plan
                    </span>
                  )}
                </div>
              </div>
              <Button
                variant="outline"
                className="w-full h-11 rounded-xl text-sm font-bold shadow-sm hover:shadow-md transition-all active:scale-[0.98]"
                onClick={() => navigate("/settings/profile")}
              >
                Edit Profile
              </Button>
            </CardContent>
          </Card>
        </section>

        {/* Preferences */}
        <section>
          <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-3 px-2">
            Preferences
          </p>
          <Card className="rounded-3xl border border-border/50 bg-card/60 backdrop-blur-xl shadow-soft overflow-hidden">
            <CardContent className="p-0 divide-y divide-border/50">
              <div className="flex items-center justify-between px-5 py-4 hover:bg-muted/30 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/5 rounded-xl text-primary">
                    <Globe className="h-4 w-4" />
                  </div>
                  <span className="text-sm font-semibold">Currency</span>
                </div>
                <Select defaultValue={user?.default_currency ?? "EUR"}>
                  <SelectTrigger className="w-[120px] h-9 rounded-xl text-sm font-medium border-border/50 bg-background/50 hover:bg-background">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c.code} value={c.code} className="rounded-lg">
                        {c.code} — {c.symbol}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between px-5 py-4 hover:bg-muted/30 transition-colors">
                <span className="text-sm font-semibold">Date Format</span>
                <Select defaultValue="DD/MM/YYYY">
                  <SelectTrigger className="w-[130px] h-9 rounded-xl text-sm font-medium border-border/50 bg-background/50 hover:bg-background">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    <SelectItem value="DD/MM/YYYY" className="rounded-lg">DD/MM/YYYY</SelectItem>
                    <SelectItem value="MM/DD/YYYY" className="rounded-lg">MM/DD/YYYY</SelectItem>
                    <SelectItem value="YYYY-MM-DD" className="rounded-lg">YYYY-MM-DD</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between px-5 py-4 hover:bg-muted/30 transition-colors">
                <span className="text-sm font-semibold">Timezone</span>
                <span className="text-sm font-medium text-muted-foreground bg-muted/50 px-2.5 py-1 rounded-md">
                  {Intl.DateTimeFormat().resolvedOptions().timeZone}
                </span>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Notifications */}
        <section>
          <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-3 px-2">
            Notifications
          </p>
          <NotificationSettings />
        </section>

        {/* Account */}
        <section>
          <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-3 px-2">
            Account
          </p>
          <Card className="rounded-3xl border border-border/50 bg-card/60 backdrop-blur-xl shadow-soft overflow-hidden">
            <CardContent className="p-0 divide-y divide-border/50">
              <button
                onClick={() => navigate("/settings/change-password")}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/5 rounded-xl text-primary">
                    <Shield className="h-4 w-4" />
                  </div>
                  <span className="text-sm font-semibold">Change Password</span>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </button>

              {BILLING_ENABLED && (
                <>
                  <button
                    onClick={() => navigate("/pricing")}
                    className="w-full flex items-center justify-between px-5 py-4 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/5 rounded-xl text-primary">
                        <CreditCard className="h-4 w-4" />
                      </div>
                      <span className="text-sm font-semibold">Subscription & Plan</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {user?.plan?.name && (
                        <span className="text-xs px-2.5 py-1 rounded-md bg-primary/10 text-primary font-bold capitalize">
                          {user.plan.name}
                        </span>
                      )}
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </button>
                  <BillingPortalButton />
                </>
              )}

              <button
                onClick={() => toast.error("Contact support to delete your account")}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-destructive/10 transition-colors group"
              >
                <span className="text-sm font-semibold text-destructive group-hover:text-destructive">Delete Account</span>
                <ChevronRight className="h-4 w-4 text-destructive/50 group-hover:text-destructive" />
              </button>
            </CardContent>
          </Card>
        </section>

        {/* Sign out */}
        <div className="pb-4">
          <Button
            variant="outline"
            className="w-full h-12 rounded-2xl text-destructive border-transparent bg-destructive/5 hover:bg-destructive/10 hover:text-destructive transition-colors font-bold shadow-none"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </div>
    </div>
  )
}
